import type Guid from "@/app/types/common/guid";
import { GuidMap, GuidQMap, GuidSet } from "@/app/types/common/guidcollections";
import appStore from "@/store/modules/appStore";
import { EnumSnackbarType } from "@/components/controls/snackbar/SnackbarTypes";
import { translateWithParams } from "@/plugins/i18n";
import type { OrderHeader } from "@/app/services/generated/orderService";
import type {
  Outlet,
  OutletSummary,
} from "@/app/services/generated/outletService";
import { wrapLoading } from "@/components/composables/componentTools";
import { groupBy } from "lodash-es";
import type { Maybe } from "@/components/utils/typeHelpers";

type TypeWithId = { id: Guid };

export function getById<T extends TypeWithId>(
  allObjects: T[] | readonly T[],
  id: Guid | null,
  idExtractor: (obj: T) => Guid = (obj) => obj.id
): T | undefined {
  // Iterating through an array we may be checking against a nullable guid,
  // so allow for them for simplicity of use
  if (id === null) {
    return undefined;
  }

  return allObjects.find((obj) => idExtractor(obj).equals(id));
}

/**
 * Type guard to send to .filter functions to cast away null/undefined
 */
export function isNotNullOrUndefined<T>(
  t: T | undefined | null | void
): t is T {
  return t !== undefined && t !== null;
}

export function isNullish<T>(
  t: T | undefined | null | void
): t is null | undefined | void {
  return t === undefined || t === null;
}

export function filterNonUnique<T, U>(arr: T[], property: (item: T) => U): U[] {
  return [...new Set(arr.map(property))];
}

export function filterNonUniqueByProperty<T, U>(
  arr: readonly T[],
  property: (item: T) => U
): T[] {
  return arr.filter(
    (item, i, arr) => arr.findIndex((t) => property(t) === property(item)) === i
  );
}

export function filterNonUniquePrimitive<T extends number | string | boolean>(
  arr: T[]
) {
  return arr.filter(
    (currentItem, currentIndex, items) =>
      items.indexOf(currentItem) === currentIndex
  );
}

/**
 * Encodes a string to a UTF-8 byte array with a UTF-8 BOM to ensure applications (e.g. Excel) recognise it as a UTF-8 stream.
 */
export function encodeStringToUtf8WithBom(toEncode: string): Uint8Array {
  const textEncoder = new TextEncoder();
  return textEncoder.encode(
    toEncode.startsWith("\uFEFF") ? toEncode : "\uFEFF" + toEncode
  );
}

/**
 * Downloads a file from the browser with the bytes returned from fileContentsProvider.
 *
 * @param fileName Currently unused.
 * @param mimeType The mime type of the document.
 * @param fileContentsProvider A function that provides the bytes to open.
 */
export async function initiateDownloadFromBytes(
  fileName: string,
  mimeType: string,
  fileContentsProvider: () => Promise<Uint8Array>
) {
  await wrapLoading(async () => {
    const bytes = await fileContentsProvider();

    if (bytes.length === 0) {
      appStore.pushSnackbarConfig({
        text: translateWithParams("document_no_data", {
          fileName,
        }).toString(),
        timeout: 5000,
        type: EnumSnackbarType.warning,
      });
      return;
    }

    appStore.pushSnackbarConfig({
      text: translateWithParams("document_print_started", {
        fileName,
      }).toString(),
    });

    const blob = new Blob([bytes], { type: mimeType });

    const link = document.createElement("a") as HTMLAnchorElement;
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;

    // Apparently firefox doesn't initiate download on .click() unless element is in the DOM,
    // but it does work when we fire an event
    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );
  });
}

/**
 * Removes duplicates from an array of Guids. Retains ordering, removing later occurrences.
 * Note. Nulls/undefined are removed.
 */
export const distinctGuids = (a: readonly (Guid | null | undefined)[]) =>
  Array.from(new GuidSet(a.filter((entry) => !!entry) as Guid[]).keys());

export const sortedDistinct = <T extends { toString: () => string }>(
  array: Array<T>,
  compareFn?: (a: T, b: T) => number
) => {
  // Sort Once O (n log(n), then single pass through to remove duplicates.
  // More efficient than O(n log(n)) hash-table check for duplicates followed by another O(n log(n)) sort.
  if (array.length === 0) {
    return [];
  }
  // at least one item
  const a = [...array];
  a.sort(compareFn);
  const toReturn = [a[0]] as Array<T>;
  let jdx = 0;

  if (compareFn) {
    for (let idx = 1; idx < a.length; idx++) {
      if (compareFn(toReturn[jdx] as T, a[idx] as T) == 0) {
        continue;
      } else {
        toReturn.push(a[idx]);
        jdx++;
      }
    }
  } else {
    for (let idx = 1; idx < a.length; idx++) {
      if (toReturn[jdx].toString() == a[idx].toString()) {
        continue;
      } else {
        toReturn.push(a[idx]);
      }
    }
  }
  return toReturn;
};

/**
 * Converts a HTMLCanvasElement in to a png format Uint8Array
 *
 * @param elem Canvas element with image data
 * @returns Uint8Array of a png image
 */
export async function canvasElementToUint8Array(
  elem: HTMLCanvasElement
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    elem.toBlob(
      (blob) =>
        blob
          ?.arrayBuffer()
          .then((buffer) => resolve(new Uint8Array(buffer)))
          .catch(reject) ?? reject
    );
  });
}

export function toMap<K, T>(
  items: readonly T[],
  keyExtractor: (item: T) => K
): Map<K, T> {
  return new Map(items.map((item) => [keyExtractor(item), item]));
}

/**
 * Creates a GuidMap from an array of objects with a function to extract the Guid key
 */
export function toGuidMap<T>(
  items: readonly T[],
  guidExtractor: (item: T) => Guid
): GuidMap<T> {
  return new GuidMap<T>(items.map((item) => [guidExtractor(item), item]));
}

export function toGuidQMap<T>(
  items: readonly T[],
  guidExtractor: (item: T) => Guid | null
): GuidQMap<T> {
  return new GuidQMap<T>(items.map((item) => [guidExtractor(item), item]));
}

export function toGuidMapMapped<TSource, TResult>(
  items: readonly TSource[],
  guidExtractor: (item: TSource) => Guid,
  extract: (item: TSource) => TResult
): GuidMap<TResult> {
  return new GuidMap<TResult>(
    items.map((item) => [guidExtractor(item), extract(item)])
  );
}

/**
 * Creates a GuidMap from an array, but groups where ids match
 */
export function toGroupedGuidMap<T>(
  items: readonly T[],
  guidExtractor: (item: T) => Guid
): GuidMap<T[]> {
  const guidMap = new GuidMap<T[]>();

  for (const item of items) {
    const itemId = guidExtractor(item);
    const existingGroup = guidMap.get(itemId);
    guidMap.set(itemId, existingGroup ? existingGroup.concat(item) : [item]);
  }

  return guidMap;
}

export function toGroupedGuidQMap<T>(
  items: readonly T[],
  guidExtractor: (item: T) => Guid | null
): GuidQMap<T[]> {
  const map = new GuidQMap<T[]>();

  for (const item of items) {
    const itemId = guidExtractor(item);
    const existingGroup = map.get(itemId);

    map.set(itemId, existingGroup ? existingGroup.concat(item) : [item]);
  }

  return map;
}

// Extracts an address from outlets or orders
export function getAddress(
  outlet: Maybe<Outlet | OutletSummary>,
  orderHeader: Maybe<OrderHeader>
) {
  let address: string[] = [];
  if (orderHeader) {
    address = [
      orderHeader.deliveryAddress1,
      orderHeader.deliveryAddress2,
      orderHeader.deliveryAddress3,
      orderHeader.deliveryAddress4,
      orderHeader.deliveryAddress5,
      orderHeader.deliveryPostcode,
      orderHeader.deliveryAddress6,
    ];
  } else if (outlet) {
    address = [
      outlet.mainAddressLine1,
      outlet.mainAddressLine2,
      outlet.mainAddressLine3,
      outlet.mainAddressLine4,
      outlet.mainAddressLine5,
      outlet.mainAddressPostcode,
      outlet.mainAddressLine6,
    ];
  }
  return address.length > 0 ? address.filter((v) => !!v).join(", ") : "-";
}

/**
 * Ensures sequential execution of a given promise.
 * If one execution fails, the queue continues processing subsequent requests.
 */
export function disallowConcurrency<T extends any[]>(
  fn: (...args: T) => PromiseLike<void>
): (...args: T) => Promise<void> {
  // The 'tail' of the queue. We ensure this always resolves successfully
  // so the chain doesn't break.
  let tail = Promise.resolve();

  return (...args: T) => {
    const nextTask = tail.then(() => fn(...args));
    tail = nextTask.catch(() => {});
    return nextTask;
  };
}

/**
 * Perform a cumulative sum on each value in an array.
 *
 * E.g. [1, 2, 3, -4] => [1, 3, 6, 2]
 */
export const cumulativeSum = (arr: number[]): number[] => {
  let sum = 0;
  return arr.map((value) => (sum += value));
};

export const sumMap = <TItem>(
  items: readonly TItem[],
  map: (item: TItem) => number
) => items.reduce((acc, item) => acc + map(item), 0);

export const round5Dp = (val: number) => Math.round(val * 100000) / 100000;
export const round3Dp = (val: number) => Math.round(val * 1000) / 1000;
export const round2Dp = (val: number) => Math.round(val * 100) / 100;

export const groupToKvp = <T, K>(
  array: readonly T[],
  extractor: (item: T) => K
) => {
  return Object.values(groupBy(array, extractor)).map((group) => ({
    key: extractor(group[0]),
    values: group,
  }));
};
